import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../Config/Config';

export default function ProductList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const filter = location.state?.filter || "In Progress";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;

  const fetchProjects = async () => {
    setLoading(true);
    try {
      let q
      if (filter === "Completed")
        q = query(collection(db, "projects-website"), where("projectStatus", "==", "Completed"), orderBy("order", "asc"));
      else
        q = query(collection(db, "projects-website"), where("projectStatus", "==", "In Progress"), orderBy("order", "asc"));
      const querySnapshot = await getDocs(q);
      const projectData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProjects(projectData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleProjectClick = (id) => {
    navigate("/products-view", { state: { id, projects } });
  };

  return (
    <section id="projects" className="relative min-h-screen py-20 bg-slate-900">
      {filter === "Completed" && (
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.h2
            className="text-4xl lg:text-5xl font-heading font-bold text-white mb-8 mt-10"
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-secondary">Completed</span> Projects
          </motion.h2>

          {loading ? (
            <div className="text-white text-center py-20">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
              {projects.map((p, i) => (
                <motion.article
                  key={p.id || i}
                  className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: disableMotion ? 0 : 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                >
                  <div
                    className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-110 overflow-hidden"
                    style={{
                      backgroundImage: `url(${p.coverImage ? p.coverImage : 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400'})`
                    }}
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-heading font-semibold text-white mb-2 line-clamp-1 group-hover:text-secondary transition-colors duration-300">
                      {p.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {p.location && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-black">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {p.location}
                        </span>
                      )}
                      {p.completedYear && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-black">
                          {p.completedYear}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white line-clamp-2 mb-4">
                      {p.description}
                    </p>
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleProjectClick(p.id)}
                        className="flex items-center space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold"
                      >
                        <span>View Details</span>
                        <svg
                          className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      )}

      {filter === "In Progress" && (
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.h2
            className="text-4xl lg:text-5xl font-heading font-bold text-white mb-8 mt-10"
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-secondary">OnGoing</span> Projects
          </motion.h2>

          {loading ? (
            <div className="text-white text-center py-20">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
              {projects.map((p, i) => (
                <motion.article
                  key={p.id || i}
                  className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: disableMotion ? 0 : 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                >
                  <div
                    className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-110 overflow-hidden"
                    style={{
                      backgroundImage: `url(${p.coverImage ? p.coverImage : 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400'})`
                    }}
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-heading font-semibold text-white mb-2 line-clamp-1 group-hover:text-secondary transition-colors duration-300">
                      {p.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {p.location && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-black">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {p.location}
                        </span>
                      )}
                      {p.completedYear && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-black">
                          {p.completedYear}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white line-clamp-2 mb-4">
                      {p.description}
                    </p>
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleProjectClick(p.id)}
                        className="flex items-center space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold"
                      >
                        <span>View Details</span>
                        <svg
                          className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
